import React from 'react';

import { Link, Text } from '@auspost/postmaster-react';

import AlertIcon from 'assets/alert.svg';
import ChevronIcon from 'assets/chevron-right.svg';

import { useShipment } from '../../hooks/shipment.hooks';
import {
  DISRUPTION_NOTICES_CHEVRON_LINK_TEST_ID,
  DISRUPTION_NOTICES_CTA_LINK_TEST_ID,
  DISRUPTION_NOTICES_HEADING,
  DISRUPTION_NOTICES_LIST_TEST_ID,
  DISRUPTION_NOTICES_TEST_ID,
} from './DisruptionNotices.test-ids';

import styles from './DisruptionNotices.module.scss';

interface DisruptionNoticeContentProps {
  heading: string;
  children: React.ReactNode;
}

const DisruptionNoticeContent: React.FC<DisruptionNoticeContentProps> = ({ heading, children }) => (
  <div className={styles.disruptionNoticeContent}>
    <AlertIcon id='DisruptionNoticesAlert' aria-label='disruption notices alert icon' role='img' />
    <div className={styles.textContent}>
      <Text variant='body-small' className={styles.heading} data-testid={DISRUPTION_NOTICES_HEADING}>
        {heading}
      </Text>
      {children}
    </div>
  </div>
);

const DisruptionNotices: React.FC = () => {
  const notices = useShipment()?.dynamicCommsList;
  const disruptionNotices = notices ? notices.filter((notice) => notice.type === 'DISRUPTION').slice(0, 3) : [];

  if (!disruptionNotices || disruptionNotices.length < 1) return null;

  if (disruptionNotices.length === 1) {
    const notice = disruptionNotices[0];
    const cta =
      notice.linkText && notice.linkUrl ? (
        <Text variant='body-small' className={styles.ctaLink}>
          <Link href={notice.linkUrl} target='_blank' data-testid={DISRUPTION_NOTICES_CTA_LINK_TEST_ID}>
            {notice.linkText}
          </Link>
        </Text>
      ) : null;

    const ChevronLinkWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
      if (cta || !notice.url) return <>{children}</>;
      return (
        <a
          className={styles.containerChevronLink}
          href={notice.url}
          data-testid={DISRUPTION_NOTICES_CHEVRON_LINK_TEST_ID}
        >
          {children}
        </a>
      );
    };

    return (
      <ChevronLinkWrapper>
        <div className={styles.disruptionNotice} data-testid={DISRUPTION_NOTICES_TEST_ID}>
          <DisruptionNoticeContent heading={notice.title}>
            <Text variant='body-small' className={styles.content}>
              {notice.content}
            </Text>
            {cta}
          </DisruptionNoticeContent>
          {!cta && notice.url && (
            <div className={styles.chevronWrapper}>
              <ChevronIcon className={styles.chevron} />
            </div>
          )}
        </div>
      </ChevronLinkWrapper>
    );
  } else {
    return (
      <div className={styles.disruptionNotice} data-testid={DISRUPTION_NOTICES_TEST_ID}>
        <DisruptionNoticeContent heading='Disruptions on this journey:'>
          <ul data-testid={DISRUPTION_NOTICES_LIST_TEST_ID}>
            {disruptionNotices.map((notice, index) => (
              <li key={index}>
                <Text variant='body-small' className={styles.content}>
                  {/* TODO: require further investigation on stacking disruption notices with empty link or default link */}
                  <Link href={notice.url || ''}>{notice.title}</Link>
                </Text>
              </li>
            ))}
          </ul>
        </DisruptionNoticeContent>
      </div>
    );
  }
};

export default DisruptionNotices;
